import { useEffect, useRef, useState } from 'react';

import { MessagesSquare, Send } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { formatarDataHora } from '@/nucleo/formato/datas';
import { cn } from '@/nucleo/utilitarios';
import { Alert, AlertDescription, AlertTitle } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Label } from '@/ui/base/label';
import { Textarea } from '@/ui/base/textarea';
import { ErrorState } from '@/ui/ErrorState';
import { Spinner } from '@/ui/Spinner';

import type { AutorDaMensagem, MensagemDaMedicao } from './contrato';
import { useMensagensDaMedicao } from './dados';

/**
 * A conversa de uma medição, vista pelo lado da SME.
 *
 * É o `MedicaoChat` do protótipo com duas diferenças conscientes:
 *
 * 1. "Enviar" NÃO GRAVA. Não há rota de mensagens em `apps/api/src/modulos/`;
 *    o botão mostra o aviso de indisponibilidade, no padrão de
 *    `SlaAprovacaoSmePage`. E há um aviso FIXO acima do campo: aviso que some
 *    deixa quem audita uma captura de tela concluir que a mensagem foi
 *    enviada — mesmo padrão do portal público e de `/sme/revisoes`.
 * 2. Abrir a conversa NÃO MARCA COMO LIDA. No protótipo, abrir dispara a
 *    marcação; aqui isso seria escrita. O contador de não lidas fica como o
 *    banco o deixaria sem a rota: intacto.
 */

/** DTP e admin falam pelo mesmo lado da SME — o protótipo os alinha juntos. */
const LADO_DA_SME: ReadonlySet<AutorDaMensagem> = new Set(['SME', 'DTP', 'ADMIN']);

const ROTULO_DO_AUTOR: Record<AutorDaMensagem, string> = {
  SME: 'SME',
  CREDENCIADO: 'Credenciado',
  DTP: 'DTP',
  ADMIN: 'Admin',
};

interface ConversaDaMedicaoPainelProps {
  medicaoId: string;
}

export function ConversaDaMedicaoPainel({ medicaoId }: ConversaDaMedicaoPainelProps) {
  const consulta = useMensagensDaMedicao(medicaoId);
  const [texto, setTexto] = useState('');
  const rolagem = useRef<HTMLDivElement>(null);
  const campo = `mensagem-${medicaoId}`;

  const mensagens: MensagemDaMedicao[] = consulta.data ?? [];

  useEffect(() => {
    if (rolagem.current) {
      rolagem.current.scrollTop = rolagem.current.scrollHeight;
    }
  }, [consulta.data]);

  // O rascunho é da conversa aberta; trocar de conversa não pode carregá-lo junto.
  useEffect(() => {
    setTexto('');
  }, [medicaoId]);

  const enviar = () => {
    if (!texto.trim()) {
      return;
    }
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  };

  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessagesSquare className="h-4 w-4" aria-hidden="true" />
          Mensagens
          {mensagens.length > 0 && (
            <Badge variant="secondary" className="ml-1">
              {mensagens.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          ref={rolagem}
          className="h-72 space-y-3 overflow-y-auto rounded-xl border border-border bg-muted/30 p-3"
        >
          {consulta.isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : consulta.isError ? (
            <ErrorState
              variant="load-failed"
              title="Não foi possível carregar esta conversa"
              primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
            />
          ) : mensagens.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center text-sm text-muted-foreground">
              <MessagesSquare className="mb-2 h-8 w-8 opacity-40" aria-hidden="true" />
              Nenhuma mensagem nesta medição ainda.
            </div>
          ) : (
            <ol className="space-y-3" aria-label="Mensagens da conversa">
              {mensagens.map((mensagem) => {
                const minha = LADO_DA_SME.has(mensagem.autorPapel);
                return (
                  <li
                    key={mensagem.id}
                    className={cn('flex flex-col', minha ? 'items-end' : 'items-start')}
                  >
                    <div
                      className={cn(
                        'max-w-[85%] rounded-2xl px-3.5 py-2 text-sm shadow-xs',
                        minha
                          ? 'rounded-tr-sm bg-primary text-primary-foreground'
                          : 'rounded-tl-sm border border-border bg-card',
                      )}
                    >
                      <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
                        <span
                          className={cn(
                            'rounded px-1.5 text-[10px] font-semibold uppercase tracking-wider',
                            minha
                              ? 'bg-primary-foreground/15 text-primary-foreground'
                              : 'bg-primary text-primary-foreground',
                          )}
                        >
                          {ROTULO_DO_AUTOR[mensagem.autorPapel]}
                        </span>
                        <span
                          className={cn(
                            'text-[11px]',
                            minha ? 'text-primary-foreground/80' : 'text-muted-foreground',
                          )}
                        >
                          {formatarDataHora(mensagem.criadoEm)}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap wrap-break-word leading-relaxed">
                        {mensagem.mensagem}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <Alert>
          <AlertTitle>O envio de mensagens ainda não está disponível</AlertTitle>
          <AlertDescription>
            A resposta ao credenciado entra em funcionamento junto com o restante do ciclo de
            medição. Nada do que for escrito aqui é enviado.
          </AlertDescription>
        </Alert>

        <div className="flex flex-col gap-2">
          <Label htmlFor={campo}>Sua mensagem</Label>
          <Textarea
            id={campo}
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
            placeholder="Escreva uma mensagem..."
            rows={2}
            onKeyDown={(evento) => {
              if (evento.key === 'Enter' && (evento.metaKey || evento.ctrlKey)) {
                evento.preventDefault();
                enviar();
              }
            }}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              <kbd className="rounded bg-muted px-1 py-0.5">Ctrl + Enter</kbd> para enviar
            </span>
            <Button size="sm" onClick={enviar} disabled={!texto.trim()}>
              <Send className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              Enviar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
